import * as Sentry from "@sentry/react-native";
import { t } from "@transifex/native";
import { T } from "@transifex/react";
import { router, useLocalSearchParams, usePathname } from "expo-router";
import { Pressable } from "react-native";

import { useCheckoutConfirmation } from "@/hooks/useCheckoutConfirmation";
import { useReceipt } from "@/hooks/useReceipt";

import Receipt from "@/assets/icons/Receipt";
import Screen from "@/components/layout/Screen";
import { ActiveFlexPassCard } from "@/components/shared/ActiveFlexPassCard";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { Box } from "@/components/ui/box";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useFocusEffect } from "@react-navigation/native";
import { useCallback } from "react";

export default function PurchaseConfirmation() {
  const { isPurchasingPass, requiresPayment, session_id, termsAndConditionsOKTimestamp, rateID, session_token } = useLocalSearchParams<{
    isPurchasingPass: string;
    requiresPayment: string;
    session_id?: string;
    termsAndConditionsOKTimestamp: string;
    rateID: string;
    session_token?: string;
  }>();
  const isPurchasingPassBoolean = isPurchasingPass === "true";
  const pathName = usePathname();

  if (!session_id && requiresPayment === "true") {
    Sentry.captureMessage("session_id is undefined");
    throw new Error("session_id is undefined");
  }

  const {
    receiptID,
    purchasePassData,
    contactInfo,
    refetchContact,
    isPending: isPaymentPending,
  } = useCheckoutConfirmation(isPurchasingPassBoolean, termsAndConditionsOKTimestamp, rateID, session_id, session_token);
  const { formattedEndTime, isPending, receipt } = useReceipt(receiptID);

  useFocusEffect(
    useCallback(() => {
      if (pathName.startsWith("/purchase/confirmation")) {
        refetchContact();
      }
    }, [pathName, refetchContact]),
  );

  if (isPending || isPaymentPending) {
    return <PurchaseConfirmationScreenSkeleton isFlexPass={isPurchasingPassBoolean} />;
  }

  return (
    <Screen>
      {/* FLEXPARK */}
      {isPurchasingPassBoolean ? (
        <VStack className="gap-[32px]">
          <PageTitle title={t("Checkout Complete!")} />
          <Box>
            <Text className="text-center text-typography-900 text-[16px] leading-[22px] align-middle">
              <T _str="Ready to park now?" />
            </Text>
            <Text className="text-center text-typography-900 text-[16px] leading-[22px] align-middle">
              <T _str="Redeem your pass below." />
            </Text>
          </Box>

          {purchasePassData && (
            <ActiveFlexPassCard
              name={purchasePassData.pass.passName}
              usedPasses={purchasePassData.pass.uses}
              maxPassUses={purchasePassData.pass.maxUses}
              passNotes={purchasePassData.pass.passNotes}
              passID={purchasePassData.pass.passID}
              canRedeemNow={purchasePassData.canRedeemNow}
            />
          )}

          <Box className="gap-[4px]">
            <Text className="text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">
              <T _str="Your receipt has been sent to:" />
            </Text>
            <Text className="font-bold text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">{contactInfo}</Text>
          </Box>

          <AppButton
            onPress={() => router.push({ pathname: "/receipt", params: { receiptID } })}
            title={t("View Receipt")}
            variant="outline"
            icon={Receipt}
            iconPosition="before"
          />
          <Pressable onPress={() => router.replace("/welcome")}>
            <Text className="text-typography-900 text-[14px] leading-[20px] text-center font-[700] underline decoration-solid">
              <T _str="Return to purchase menu" />
            </Text>
          </Pressable>
        </VStack>
      ) : (
        // SINGLE PASS
        <VStack className="gap-[32px]">
          <PageTitle title={t("Checkout Complete!")} />
          <Box>
            <Text className="text-center text-typography-900 text-[16px] leading-[22px] align-middle">
              <T _str="Your parking pass at {locationName} has been activated. You’re all set!" locationName={receipt?.locationName} />
            </Text>
          </Box>

          <Card className="gap-[16px] pt-[44px] pb-[24px] px-[20px]">
            <Text className="font-semibold text-[16px] leading-[22px]  text-center align-middle text-typography-900">
              <T _str="This parking session ends at" />
            </Text>
            <Text className="font-bold text-[22px] leading-[100%] text-center align-middle text-typography-900">{formattedEndTime}</Text>
            <Box className="gap-[4px]">
              {contactInfo && (
                <>
                  <Text className="text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">
                    <T _str="Your receipt has been sent to:" />
                  </Text>
                  <Text className="font-bold text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">{contactInfo}</Text>
                </>
              )}
            </Box>
            <Box className="pt-[24px]">
              <AppButton
                onPress={() => router.push({ pathname: "/receipt", params: { receiptID } })}
                title={t("View Receipt")}
                variant="primary"
                icon={Receipt}
                iconPosition="before"
              />
            </Box>
          </Card>

          <Pressable onPress={() => router.replace("/welcome")}>
            <Text className="text-typography-900 text-[14px] leading-[20px] text-center font-bold underline decoration-solid">
              <T _str="Return to purchase menu" />
            </Text>
          </Pressable>
        </VStack>
      )}
    </Screen>
  );
}

function PurchaseConfirmationScreenSkeleton({ isFlexPass = false }: Readonly<{ isFlexPass?: boolean }>) {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <SkeletonText className="h-[24px] w-[60%] self-center" />
        <SkeletonText _lines={2} className="h-[16px] w-[80%] self-center" />

        {isFlexPass ? (
          <>
            <Card className="py-[24px]">
              <VStack className="gap-[20px]">
                <SkeletonText className="h-[12px] w-[160px]" />
                <SkeletonText className="h-[20px] w-[160px]" />
                <VStack className="gap-[10px]">
                  <SkeletonText _lines={4} className="h-[14px] w-full" />
                </VStack>
              </VStack>
              <Skeleton className="h-[52px] rounded-[6px]" />
            </Card>
            <SkeletonText _lines={2} className="h-[14px] w-[70%] self-center" />
            <Skeleton className="h-[52px] rounded-[6px]" />
            <SkeletonText className="h-[14px] w-[60%] self-center" />
          </>
        ) : (
          <>
            <Card>
              <SkeletonText className="h-[16px] w-[80%] self-center" />
              <SkeletonText className="h-[30px] w-[40%] self-center" />
              <SkeletonText _lines={2} className="h-[16px] w-[80%] self-center" />
              <Skeleton className="h-[52px] w-full mt-[24px]" />
            </Card>
            <SkeletonText className="h-[14px] w-[60%] self-center" />
          </>
        )}
      </VStack>
    </Screen>
  );
}
